"use client";

import Link from "next/link";
import Image from "next/image";
import { useSession } from "next-auth/react";
import { usePathname, useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { useState, useEffect, useRef } from "react";
import { PlusCircle, User, Settings, LogOut, LayoutDashboard, Trophy, Building2 } from "lucide-react";
import type { SubscriptionLimits } from "@/lib/subscription-limits";
import { clientLogger } from "@/lib/client-logger";
import { useWhitelabelVisibility } from "@/hooks/useWhitelabelVisibility";
import { SignOutButton } from "@/components/auth/SignOutButton";

interface CampaignListItem {
  id: string;
  status: string;
}

/** Custom navigation menu item */
export interface NavMenuItem {
  id: string;
  type: 'TENANT_PAGE' | 'CAMPAIGN' | 'CUSTOM';
  label: string;
  customUrl?: string | null;
  openInNewTab: boolean;
  campaignSlug?: string | null;
}

/**
 * Props for the Navigation component
 */
export interface NavigationProps {
  /** Tenant configuration for whitelabel mode */
  tenant?: {
    name?: string;
    slug?: string;
    logoUrl?: string | null;
    textColor?: string | null;
    primaryColor?: string | null;
    // Header customization
    headerBackgroundColor?: string | null;
    headerTextColor?: string | null;
    headerLinkColor?: string | null;
    headerSticky?: boolean;
    headerTransparentInitial?: boolean;
    headerTransparentLogoUrl?: string | null;
    headerScrolledBackgroundColor?: string | null;
    headerScrolledTextColor?: string | null;
    // Header button colors
    headerButtonBackgroundColor?: string | null;
    headerButtonTextColor?: string | null;
    headerScrolledButtonBackgroundColor?: string | null;
    headerScrolledButtonTextColor?: string | null;
    // Custom navigation menu items
    navMenuItems?: NavMenuItem[];
  } | null;
}

/**
 * Navigation component with whitelabel support
 *
 * In SaaS mode (whitelabel disabled):
 * - Shows standard SaaS branding
 * - Shows Create Experience button for all users
 * - Shows Campaigns and Digital Assets links
 *
 * In whitelabel mode:
 * - Shows tenant's logo if available
 * - Hides Create Experience button for non-admin users
 * - Hides Campaigns link for non-admin users (they only see hunts they participate in)
 */
export function Navigation({ tenant }: NavigationProps = {}) {
  const { data: session, status } = useSession();
  const pathname = usePathname();
  const router = useRouter();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAvatarMenuOpen, setIsAvatarMenuOpen] = useState(false);
  const [subscriptionLimits, setSubscriptionLimits] = useState<SubscriptionLimits | null>(null);
  const [activeCampaigns, setActiveCampaigns] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [reservedCount, setReservedCount] = useState(0);
  const avatarMenuRef = useRef<HTMLDivElement>(null);

  // Close avatar dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (avatarMenuRef.current && !avatarMenuRef.current.contains(event.target as Node)) {
        setIsAvatarMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch reserved claims count
  useEffect(() => {
    if (session?.user) {
      fetch('/api/my-assets/reserved-count')
        .then(res => res.json())
        .then(data => setReservedCount(data.count || 0))
        .catch(() => {});
    }
  }, [session]);

  // Get user role for visibility logic
  const userRole = session?.user?.tenants?.[0]?.role;
  const visibility = useWhitelabelVisibility(userRole);
  const { showCreateHunt, showMyCampaigns: _showMyCampaigns, showBrowseHunts, showHowItWorks, isWhitelabelMode } = visibility;

  // Check if we're on the homepage (root only)
  // Only the root "/" gets transparent initial treatment, not tenant pages
  const isOnHomepage = pathname === '/';

  // Fetch subscription limits if user is logged in and subscriptions are visible
  useEffect(() => {
    const fetchLimits = async () => {
      if (!session?.user || !showCreateHunt) return;

      try {
        // Get subscription info
        const subResponse = await fetch('/api/subscription/status');
        if (subResponse.ok) {
          const subData = await subResponse.json();
          setSubscriptionLimits(subData.subscription?.limits);
        }

        // Get active campaigns count
        const campaignsResponse = await fetch('/api/campaigns');
        if (campaignsResponse.ok) {
          const campaignsData = await campaignsResponse.json();
          const activeCount = campaignsData.campaigns?.filter((c: CampaignListItem) =>
            c.status === 'PUBLISHED' || c.status === 'ACTIVE'
          ).length || 0;
          setActiveCampaigns(activeCount);
        }
      } catch (error) {
        clientLogger.error("Error fetching limits", { error });
      }
    };

    fetchLimits();
  }, [session, showCreateHunt]);

  // Scroll detection for transparent initial header
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 50;
      setIsScrolled(scrolled);
    };

    // Initial check
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCreateHunt = () => {
    // If not logged in, redirect to signup
    if (!session) {
      router.push('/auth/signup');
      return;
    }

    // Check if user has hit their campaign limit
    if (subscriptionLimits && subscriptionLimits.activeCampaigns !== null && subscriptionLimits.activeCampaigns !== -1) {
      if (activeCampaigns >= subscriptionLimits.activeCampaigns) {
        // Redirect to upgrade page
        router.push(`/dashboard/upgrade?message=${encodeURIComponent('You have reached your campaign limit. Upgrade to create more campaigns.')}&current=${activeCampaigns}&limit=${subscriptionLimits.activeCampaigns}&returnUrl=/dashboard/campaigns/new`);
        return;
      }
    }

    // User is logged in and has capacity, go to campaign creation
    router.push('/dashboard/campaigns/new');
  };

  // Determine brand display
  const brandName = isWhitelabelMode && tenant?.name ? tenant.name : 'DA SaaS';

  // Header settings with defaults
  const headerSticky = tenant?.headerSticky ?? false;
  const headerTransparentInitial = tenant?.headerTransparentInitial ?? false;
  const headerBackgroundColor = tenant?.headerBackgroundColor || '#FFFFFF';
  const headerTextColor = tenant?.headerTextColor || (isWhitelabelMode ? '#111827' : '#6B7280');
  const headerLinkColor = tenant?.headerLinkColor || (isWhitelabelMode ? '#3B82F6' : '#6B7280');
  const headerScrolledBackgroundColor = tenant?.headerScrolledBackgroundColor || '#FFFFFF';
  const headerScrolledTextColor = tenant?.headerScrolledTextColor || (isWhitelabelMode ? '#111827' : '#6B7280');
  // Button colors
  const headerButtonBackgroundColor = tenant?.headerButtonBackgroundColor || '#3B82F6';
  const headerButtonTextColor = tenant?.headerButtonTextColor || '#FFFFFF';
  const headerScrolledButtonBackgroundColor = tenant?.headerScrolledButtonBackgroundColor || '#3B82F6';
  const headerScrolledButtonTextColor = tenant?.headerScrolledButtonTextColor || '#FFFFFF';

  // Determine current header colors based on scroll state (for transparent initial mode)
  // In SaaS mode, homepage always starts transparent. In whitelabel, it depends on tenant config.
  const isInTransparentState = (isWhitelabelMode ? headerTransparentInitial : isOnHomepage) && !isScrolled && isOnHomepage;

  // SaaS mode transparent state colors (gray text/links)
  const saasTransparentTextColor = '#6B7280';
  const saasTransparentLinkColor = '#6B7280';
  const saasTransparentButtonBg = 'transparent';
  const saasTransparentButtonText = '#6B7280';

  // Determine logo based on transparent state
  const defaultLogoUrl = tenant?.logoUrl || null;
  const transparentLogoUrl = tenant?.headerTransparentLogoUrl || null;
  // Use transparent logo when in transparent state, fall back to default logo
  const brandLogoUrl = isInTransparentState && transparentLogoUrl ? transparentLogoUrl : defaultLogoUrl;

  // Use scrolled colors whenever we're NOT in transparent state
  const shouldUseScrolledColors = !isInTransparentState;

  const currentBackgroundColor = isInTransparentState
    ? 'transparent'
    : (shouldUseScrolledColors ? headerScrolledBackgroundColor : headerBackgroundColor);
  const currentTextColor = isInTransparentState
    ? (isWhitelabelMode ? headerTextColor : saasTransparentTextColor)
    : (shouldUseScrolledColors ? headerScrolledTextColor : headerTextColor);
  const currentLinkColor = isInTransparentState
    ? (isWhitelabelMode ? headerLinkColor : saasTransparentLinkColor)
    : (shouldUseScrolledColors ? headerScrolledTextColor : headerLinkColor);
  const currentButtonBackgroundColor = isInTransparentState
    ? (isWhitelabelMode ? headerButtonBackgroundColor : saasTransparentButtonBg)
    : (shouldUseScrolledColors ? headerScrolledButtonBackgroundColor : headerButtonBackgroundColor);
  const currentButtonTextColor = isInTransparentState
    ? (isWhitelabelMode ? headerButtonTextColor : saasTransparentButtonText)
    : (shouldUseScrolledColors ? headerScrolledButtonTextColor : headerButtonTextColor);

  // Helper function for link text colors
  const getLinkClasses = () => {
    return 'inline-flex items-center px-1 pt-1 text-sm font-medium opacity-80 hover:opacity-100';
  };

  const getLinkStyle = () => {
    return {
      color: currentLinkColor,
      transition: 'color 0.3s ease',
    };
  };

  // Get text style (for non-link text elements)
  const getTextStyle = () => {
    return {
      color: currentTextColor || '#111827',
      transition: 'color 0.3s ease',
    };
  };

  // Get button style for sign in/out buttons
  const getButtonStyle = (): React.CSSProperties => {
    return {
      backgroundColor: currentButtonBackgroundColor,
      color: currentButtonTextColor,
      borderColor: currentButtonBackgroundColor,
      transition: 'background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease',
    };
  };

  // Get custom menu items from tenant
  const customMenuItems = tenant?.navMenuItems || [];

  // Helper function to get the URL for a menu item
  const getMenuItemUrl = (item: NavMenuItem): string => {
    switch (item.type) {
      case 'TENANT_PAGE':
        return tenant?.slug ? `/${tenant.slug}` : '/';
      case 'CAMPAIGN':
        return item.campaignSlug && tenant?.slug ? `/${tenant.slug}/campaigns/${item.campaignSlug}` : '/';
      case 'CUSTOM':
        return item.customUrl || '/';
      default:
        return '/';
    }
  };

  // Render a single custom menu item
  const renderCustomMenuItem = (item: NavMenuItem, isMobile = false) => {
    const url = getMenuItemUrl(item);
    const isExternal = item.type === 'CUSTOM' && item.customUrl &&
      (item.customUrl.startsWith('http://') || item.customUrl.startsWith('https://'));

    if (isMobile) {
      return (
        <Link
          key={item.id}
          href={url}
          className="block px-3 py-2 text-base font-medium"
          style={{ color: headerScrolledTextColor }}
          onClick={() => setIsMobileMenuOpen(false)}
          {...(isExternal && item.openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {item.label}
        </Link>
      );
    }

    return (
      <Link
        key={item.id}
        href={url}
        className={getLinkClasses()}
        style={getLinkStyle()}
        {...(isExternal && item.openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {item.label}
      </Link>
    );
  };

  // Build nav classes based on mode and settings
  const getNavClasses = () => {
    const classes: string[] = [];

    if (isWhitelabelMode) {
      if (headerSticky) {
        classes.push('fixed top-0 left-0 right-0 z-50');
      }
    } else {
      // SaaS mode - sticky on all pages
      classes.push('fixed top-0 left-0 right-0 z-50');
    }

    if (!isInTransparentState) {
      classes.push('shadow-sm');
    }

    return classes.join(' ');
  };

  // Build nav inline style (both modes use scroll-based transitions)
  const getNavStyle = (): React.CSSProperties => {
    return {
      backgroundColor: currentBackgroundColor,
      transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
    };
  };

  return (
    <nav
      className={getNavClasses()}
      style={getNavStyle()}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link
              href="/"
              className="flex items-center"
              style={getTextStyle()}
            >
              {brandLogoUrl ? (
                <Image
                  src={brandLogoUrl}
                  alt={brandName}
                  width={150}
                  height={40}
                  className="h-10 w-auto"
                  unoptimized
                />
              ) : (
                <span className="text-xl font-bold">{brandName}</span>
              )}
            </Link>
            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
              {/* Custom menu items from tenant settings - shown first */}
              {isWhitelabelMode && customMenuItems.map(item => renderCustomMenuItem(item))}
              {session ? (
                // Authenticated user navigation
                <>
                  <Link href="/my-assets" className={getLinkClasses()} style={getLinkStyle()}>
                    My Assets
                    {reservedCount > 0 && (
                      <span className="ml-1.5 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
                        {reservedCount}
                      </span>
                    )}
                  </Link>
                  {showBrowseHunts && (
                    <Link href="/browse" className={getLinkClasses()} style={getLinkStyle()}>
                      Browse Experiences
                    </Link>
                  )}
                </>
              ) : (
                // Public navigation
                <>
                  {showBrowseHunts && (
                    <Link href="/browse" className={getLinkClasses()} style={getLinkStyle()}>
                      Browse Experiences
                    </Link>
                  )}
                  {showHowItWorks && (
                    <Link href="/how-it-works" className={getLinkClasses()} style={getLinkStyle()}>
                      How it Works
                    </Link>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="hidden sm:flex sm:items-center sm:space-x-4">
            {/* Create Experience button - hidden in whitelabel mode (use dashboard instead) */}
            {showCreateHunt && !isWhitelabelMode && (
              <Button
                onClick={handleCreateHunt}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                size="sm"
              >
                <PlusCircle className="w-4 h-4 mr-2" />
                Create Experience
              </Button>
            )}

            {status === "loading" ? (
              <div className="animate-pulse">
                <div className="h-8 w-8 bg-gray-200 rounded-full"></div>
              </div>
            ) : session ? (
              // Authenticated user - avatar dropdown
              <div className="relative" ref={avatarMenuRef}>
                <button
                  onClick={() => setIsAvatarMenuOpen((prev) => !prev)}
                  className="flex items-center justify-center w-9 h-9 rounded-full text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors overflow-hidden"
                  style={!session.user.profileImageUrl ? {
                    backgroundColor: `${currentTextColor}20`,
                    color: currentTextColor,
                    transition: 'background-color 0.3s ease, color 0.3s ease',
                  } : undefined}
                  aria-label="User menu"
                >
                  {session.user.profileImageUrl ? (
                    <Image
                      src={session.user.profileImageUrl}
                      alt="Profile"
                      width={36}
                      height={36}
                      className="w-full h-full object-cover"
                      unoptimized
                    />
                  ) : session.user.firstName && session.user.lastName
                    ? `${session.user.firstName[0]}${session.user.lastName[0]}`.toUpperCase()
                    : <User className="w-4 h-4" />}
                </button>

                {isAvatarMenuOpen && (
                  <div className="absolute right-0 z-50 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
                    {/* User info */}
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">
                        {session.user.firstName} {session.user.lastName}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {session.user.email}
                      </p>
                      {session.user.tenants && session.user.tenants.length > 0 && (
                        <span className="inline-block mt-1.5 text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                          {session.user.tenants[0].name}
                        </span>
                      )}
                    </div>

                    {/* Menu items */}
                    <div className="py-1">
                      <Link
                        href="/my-assets"
                        className="flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => setIsAvatarMenuOpen(false)}
                      >
                        <Trophy className="w-4 h-4 text-gray-400" />
                        My Assets
                        {reservedCount > 0 && (
                          <span className="ml-auto inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
                            {reservedCount}
                          </span>
                        )}
                      </Link>
                      {session.user.tenants?.[0]?.slug && (
                        <Link
                          href={`/${session.user.tenants[0].slug}`}
                          className="flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => setIsAvatarMenuOpen(false)}
                        >
                          <Building2 className="w-4 h-4 text-gray-400" />
                          My Organization
                        </Link>
                      )}
                      <Link
                        href="/dashboard"
                        className="flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => setIsAvatarMenuOpen(false)}
                      >
                        <LayoutDashboard className="w-4 h-4 text-gray-400" />
                        Dashboard
                      </Link>
                      <Link
                        href="/dashboard/settings"
                        className="flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => setIsAvatarMenuOpen(false)}
                      >
                        <Settings className="w-4 h-4 text-gray-400" />
                        Settings
                      </Link>
                    </div>

                    {/* Sign out */}
                    <div className="border-t border-gray-100 py-1">
                      <SignOutButton
                        onBeforeSignOut={() => setIsAvatarMenuOpen(false)}
                        className="flex items-center gap-2.5 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <LogOut className="w-4 h-4 text-gray-400" />
                        Sign out
                      </SignOutButton>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // Unauthenticated user
              <Link
                href="/auth/signin"
                className="text-sm font-medium px-4 py-2 rounded opacity-90 hover:opacity-100"
                style={getButtonStyle()}
              >
                Sign In
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="sm:hidden flex items-center">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              style={{
                color: isInTransparentState ? headerTextColor : headerScrolledTextColor,
                transition: 'color 0.3s ease',
              }}
              aria-expanded="false"
            >
              <span className="sr-only">Open main menu</span>
              {!isMobileMenuOpen ? (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              ) : (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div
            className="sm:hidden"
            style={{
              backgroundColor: headerScrolledBackgroundColor,
            }}
          >
            {/* Create Experience button for mobile - hidden in whitelabel mode (use dashboard instead) */}
            {showCreateHunt && !isWhitelabelMode && (
              <div className="px-3 pt-2 pb-3">
                <Button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    handleCreateHunt();
                  }}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Create Experience
                </Button>
              </div>
            )}

            <div
              className="pt-2 pb-3 space-y-1 border-t"
              style={{ borderColor: `${headerScrolledTextColor}20` }}
            >
              {/* Custom menu items from tenant settings - shown first */}
              {isWhitelabelMode && customMenuItems.map(item => renderCustomMenuItem(item, true))}
              {session ? (
                // Authenticated user mobile navigation
                <>
                  <Link
                    href="/my-assets"
                    className="flex items-center px-3 py-2 text-base font-medium"
                    style={{ color: headerScrolledTextColor }}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    My Assets
                    {reservedCount > 0 && (
                      <span className="ml-1.5 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
                        {reservedCount}
                      </span>
                    )}
                  </Link>
                  <Link
                    href="/dashboard"
                    className="block px-3 py-2 text-base font-medium"
                    style={{ color: headerScrolledTextColor }}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  {showBrowseHunts && (
                    <Link
                      href="/browse"
                      className="block px-3 py-2 text-base font-medium"
                      style={{ color: headerScrolledTextColor }}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Browse Experiences
                    </Link>
                  )}
                  <Link
                    href="/dashboard/settings"
                    className="block px-3 py-2 text-base font-medium"
                    style={{ color: headerScrolledTextColor }}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Settings
                  </Link>
                </>
              ) : (
                // Public mobile navigation
                <>
                  {showBrowseHunts && (
                    <Link
                      href="/browse"
                      className="block px-3 py-2 text-base font-medium"
                      style={{ color: headerScrolledTextColor }}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Browse Experiences
                    </Link>
                  )}
                  {showHowItWorks && (
                    <Link
                      href="/how-it-works"
                      className="block px-3 py-2 text-base font-medium"
                      style={{ color: headerScrolledTextColor }}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      How it Works
                    </Link>
                  )}
                </>
              )}
            </div>

            <div
              className="pt-4 pb-3 border-t"
              style={{ borderColor: `${headerScrolledTextColor}20` }}
            >
              {session ? (
                <div className="space-y-1">
                  <div className="px-3 py-2">
                    <div
                      className="text-base font-medium"
                      style={{ color: headerScrolledTextColor }}
                    >
                      {session.user.firstName} {session.user.lastName}
                    </div>
                    <div
                      className="text-sm"
                      style={{ color: `${headerScrolledTextColor}99` }}
                    >
                      {session.user.email}
                    </div>
                    {session.user.tenants && session.user.tenants.length > 0 && (
                      <div
                        className="text-xs px-2 py-1 rounded mt-1 inline-block"
                        style={{
                          backgroundColor: `${headerScrolledTextColor}15`,
                          color: headerScrolledTextColor
                        }}
                      >
                        {session.user.tenants[0].name}
                      </div>
                    )}
                  </div>
                  <SignOutButton
                    onBeforeSignOut={() => setIsMobileMenuOpen(false)}
                    className="block w-full text-left px-3 py-2 text-base font-medium"
                    style={{ color: headerScrolledTextColor }}
                  >
                    Sign out
                  </SignOutButton>
                </div>
              ) : (
                <div className="space-y-1">
                  <Link
                    href="/auth/signin"
                    className="block px-3 py-2 text-base font-medium"
                    style={{ color: headerScrolledTextColor }}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Sign In
                  </Link>
                  <Link
                    href="/auth/signup"
                    className="block px-3 py-2 text-base font-medium rounded-md mx-3"
                    style={{
                      backgroundColor: headerScrolledButtonBackgroundColor,
                      color: headerScrolledButtonTextColor
                    }}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Get Started
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
